import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom'; 
import API from '../services/api';

const AlumniDirectory = () => {
    const [alumni, setAlumni] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const location = useLocation();

    // State to track which alumni card was clicked for the popup
    const [selectedAlumni, setSelectedAlumni] = useState(null);

    useEffect(() => {
        // 1. Fetch all alumni
        API.get('/get_alumni.php')
            .then(res => {
                // SAFETY CHECK: Only set data if it is actually an Array!
                if (Array.isArray(res.data)) {
                    setAlumni(res.data);
                } else {
                    console.error("Database Error:", res.data);
                    setAlumni([]); // Forces an empty array to prevent .filter() from crashing
                }
                
                setLoading(false);
                
                // 2. Sync with Header search if a query exists in URL
                const params = new URLSearchParams(location.search);
                const query = params.get('query');
                if (query) {
                    setSearchTerm(query);
                }
            })
            .catch(err => {
                console.error("Directory fetch error:", err);
                setAlumni([]); // Safety net for network errors
                setLoading(false);
            });
    }, [location.search]);

    // Helper for initials avatar
    const getInitials = (name) => {
        if (!name) return "SA";
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    // Helper to get local profile pic
    const getProfilePicUrl = (picPath) => {
        if (!picPath) return null;
        return `https://sherianalumni.com/api/${picPath}`;
    };

   // 🌟 EXPANDED SEARCH LOGIC: Now checks Address, Skills, and Achievements fields!
   const filteredAlumni = (Array.isArray(alumni) ? alumni : []).filter(item => {
        const search = searchTerm.toLowerCase();
        
        // Safely convert all fields to lowercase strings (so null/empty values don't crash)
        const nameStr = item.name ? String(item.name).toLowerCase() : "";
        const kitStr = item.kit_number ? String(item.kit_number).toLowerCase() : "";
        const wingStr = item.wing ? String(item.wing).toLowerCase() : "";
        const jobStr = item.job_status ? String(item.job_status).toLowerCase() : "";
        const orgStr = item.organization ? String(item.organization).toLowerCase() : "";
        const profStr = item.profession ? String(item.profession).toLowerCase() : "";
        const desigStr = item.designation ? String(item.designation).toLowerCase() : "";
        const instStr = item.institute ? String(item.institute).toLowerCase() : "";
        const eduStr = item.education ? String(item.education).toLowerCase() : "";
        
        const currentAddrStr = item.current_address ? String(item.current_address).toLowerCase() : "";
        const permanentAddrStr = item.permanent_address ? String(item.permanent_address).toLowerCase() : "";
        
        // NEW: Added Skills and Achievements to search
        const skillsStr = item.skills ? String(item.skills).toLowerCase() : "";
        const achievementsStr = item.achievements ? String(item.achievements).toLowerCase() : "";

        // If the search term is found in ANY of these fields, return true!
        return nameStr.includes(search) || 
               kitStr.includes(search) || 
               wingStr.includes(search) ||
               jobStr.includes(search) ||
               orgStr.includes(search) ||
               profStr.includes(search) ||
               desigStr.includes(search) ||
               instStr.includes(search) ||
               eduStr.includes(search) ||
               currentAddrStr.includes(search) || 
               permanentAddrStr.includes(search) ||
               skillsStr.includes(search) ||
               achievementsStr.includes(search);
    });

    return (
        <div className="directory-wrapper">
            <style>{`
                .directory-wrapper {
                    padding-top: 120px;
                    background: #f4f7f6;
                    min-height: 100vh;
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    padding-bottom: 80px;
                    overflow-x: hidden;
                }

                /* --- SEARCH SECTION --- */
                .search-hero {
                    max-width: 800px;
                    margin: 0 auto 60px;
                    text-align: center;
                    padding: 0 20px;
                    animation: fadeInDown 0.8s ease-out;
                }

                .search-hero h2 {
                    color: #134628;
                    font-weight: 900;
                    font-size: clamp(2rem, 5vw, 3rem);
                    text-transform: uppercase;
                    margin-bottom: 25px;
                    letter-spacing: 2px;
                }

                .search-box-container {
                    position: relative;
                    box-shadow: 0 15px 35px rgba(19, 70, 40, 0.1);
                    border-radius: 50px;
                    overflow: hidden;
                }

                .search-input {
                    width: 100%;
                    padding: 20px 30px 20px 60px;
                    border-radius: 50px;
                    border: 3px solid #ffffff;
                    background: #ffffff;
                    outline: none;
                    font-size: 1.2rem;
                    transition: all 0.3s ease;
                    color: #134628;
                }

                .search-input:focus {
                    border-color: #ffc300;
                    box-shadow: 0 0 0 10px rgba(255, 195, 0, 0.1);
                }

                .search-icon-float {
                    position: absolute;
                    left: 25px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #ffc300;
                    font-size: 1.4rem;
                }

                /* --- ALUMNI GRID --- */
                .alumni-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 30px;
                    max-width: 1300px;
                    margin: 0 auto;
                    padding: 0 20px;
                }

                .alumni-card {
                    background: white;
                    border-radius: 24px;
                    padding: 40px 25px 35px;
                    text-align: center;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.03);
                    border: 1px solid #f0f0f0;
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    position: relative;
                    overflow: hidden;
                    animation: slideUpFade 0.6s ease-out backwards;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    cursor: pointer;
                }

                .alumni-card:hover {
                    transform: translateY(-12px);
                    box-shadow: 0 20px 40px rgba(19, 70, 40, 0.12);
                    border-color: rgba(255, 195, 0, 0.3);
                }

                .alumni-card::before {
                    content: '';
                    position: absolute;
                    top: 0; left: 0; width: 100%; height: 6px;
                    background: linear-gradient(90deg, #134628, #ffc300);
                    transform: scaleX(0);
                    transition: transform 0.4s ease;
                }

                .alumni-card:hover::before {
                    transform: scaleX(1);
                }

                .alumni-avatar {
                    width: 90px;
                    height: 90px;
                    background: linear-gradient(135deg, #134628, #0a2415);
                    color: #ffc300;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 2rem;
                    font-weight: 800;
                    margin: 0 auto 20px;
                    box-shadow: 0 8px 15px rgba(0,0,0,0.1);
                    overflow: hidden;
                }

                .alumni-name {
                    color: #134628;
                    font-weight: 900;
                    font-size: 1.4rem;
                    margin-bottom: 5px;
                }

                .wing-badge {
                    display: inline-block;
                    background: #f0f2f5;
                    color: #134628;
                    padding: 6px 16px;
                    border-radius: 50px;
                    font-size: 0.85rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    border: 1px solid #e1e4e8;
                    transition: 0.3s;
                }

                .alumni-card:hover .wing-badge {
                    background: #134628;
                    color: #ffc300;
                    border-color: #134628;
                }

                .view-profile-btn {
                    margin-top: 25px;
                    background: #ffc300;
                    color: #134628;
                    padding: 10px 24px;
                    border-radius: 50px;
                    font-size: 0.85rem;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    border: none;
                    transition: 0.3s ease;
                    pointer-events: none;
                }

                .alumni-card:hover .view-profile-btn {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 20px rgba(255, 195, 0, 0.4);
                    background: #e6b000;
                    gap: 12px;
                }

                /* --- ALUMNI PROFILE POPUP MODAL --- */
                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(19, 70, 40, 0.85); backdrop-filter: blur(8px); z-index: 1000; display: flex; align-items: center; justify-content: center; animation: fadeIn 0.3s; padding: 20px; }
                
                .modal-content { 
                    background: white; border-radius: 24px; padding: 40px; 
                    width: 100%; max-width: 480px; 
                    max-height: 85vh; 
                    overflow-y: auto; 
                    box-shadow: 0 25px 50px rgba(0,0,0,0.4); 
                    animation: slideUp 0.4s cubic-bezier(0.22, 1, 0.36, 1); 
                    position: relative; text-align: center; 
                }

                .modal-content::-webkit-scrollbar { width: 8px; }
                .modal-content::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 10px; }
                
                .close-modal { position: absolute; top: 20px; right: 25px; background: none; border: none; font-size: 1.5rem; color: #a0aec0; cursor: pointer; transition: 0.3s; }
                .close-modal:hover { color: #d90429; }
                
                .modal-avatar { 
                    width: 100px; height: 100px; background: linear-gradient(135deg, #ffc300, #ffb703); 
                    color: #134628; border-radius: 50%; display: flex; align-items: center; 
                    justify-content: center; font-size: 2.5rem; font-weight: 900; margin: 0 auto 20px; 
                    box-shadow: 0 10px 25px rgba(255, 195, 0, 0.3); border: 4px solid #fff; 
                    overflow: hidden; 
                }
                .modal-name { color: #134628; font-weight: 900; font-size: 1.8rem; margin-bottom: 5px; }
                
                .modal-details { text-align: left; margin-top: 25px; padding-top: 20px; border-top: 1px solid #f0f2f5; }
                .modal-detail-row { display: flex; align-items: center; padding: 12px 0; border-bottom: 1px dashed #f0f2f5; }
                .modal-detail-row:last-child { border-bottom: none; }
                .modal-detail-row i { color: #ffc300; width: 30px; font-size: 1.1rem; text-align: center; margin-right: 12px; }
                .modal-detail-row span { color: #475569; font-weight: 600; font-size: 1rem; }
                .email-link { color: #64748b; text-decoration: none; transition: color 0.3s; }
                .email-link:hover { color: #134628; font-weight: 700; }
                
                .modal-connect-btn { background: linear-gradient(45deg, #134628, #0a2415); color: white; border: none; padding: 15px; width: 100%; border-radius: 12px; font-weight: 800; font-size: 1.1rem; text-transform: uppercase; cursor: pointer; transition: 0.3s; margin-top: 25px; box-shadow: 0 10px 20px rgba(19, 70, 40, 0.15); }
                .modal-connect-btn:hover { background: #134628; transform: translateY(-3px); box-shadow: 0 15px 30px rgba(19, 70, 40, 0.25); color: #ffc300; }

                /* --- ANIMATIONS --- */
                @keyframes fadeInDown { from { opacity: 0; transform: translateY(-30px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes slideUpFade { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }

                .no-results { text-align: center; padding: 100px 20px; color: #888; }
                @media (max-width: 768px) { .search-hero h2 { font-size: 1.8rem; } .alumni-grid { grid-template-columns: 1fr; } .modal-content { padding: 30px 20px; } }
            `}</style>

            <div className="container">
                {/* HERO SEARCH AREA */}
                <div className="search-hero">
                    <h2>Sherian Directory</h2>
                    <div className="search-box-container">
                        <i className="fas fa-search search-icon-float"></i>
                        <input 
                            type="text" 
                            className="search-input" 
                            placeholder="Search by Name, Profession, Skills, City..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border" style={{color: '#134628'}} role="status"></div>
                        <p className="mt-3 text-muted fw-bold">Gathering the Sherian Family...</p>
                    </div>
                ) : (
                    <>
                        {filteredAlumni.length > 0 ? (
                            <div className="alumni-grid">
                                {filteredAlumni.map((person, index) => (
                                    <div 
                                        key={index} 
                                        className="alumni-card"
                                        style={{ animationDelay: `${index * 0.1}s` }}
                                        onClick={() => setSelectedAlumni(person)}
                                    >
                                        <div className="alumni-avatar">
                                            {person.profile_pic ? (
                                                <img 
                                                    src={getProfilePicUrl(person.profile_pic)} 
                                                    alt={person.name} 
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                                />
                                            ) : (
                                                getInitials(person.name)
                                            )}
                                        </div>
                                        <h3 className="alumni-name">{person.name}</h3>
                                        <div className="wing-badge">{person.wing} Wing</div>

                                        <button className="view-profile-btn">
                                            View Profile <i className="fas fa-arrow-right"></i>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-results">
                                <i className="fas fa-user-slash mb-3" style={{fontSize: '4rem', opacity: 0.2}}></i>
                                <h3>No Alumni Found</h3>
                                <p>Try searching with a different term.</p>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ALUMNI FULL PROFILE POPUP MODAL */}
            {selectedAlumni && (
                <div className="modal-overlay" onClick={() => setSelectedAlumni(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        
                        <button className="close-modal" onClick={() => setSelectedAlumni(null)}>
                            <i className="fas fa-times"></i>
                        </button>

                        <div className="modal-avatar">
                            {selectedAlumni.profile_pic ? (
                                <img 
                                    src={getProfilePicUrl(selectedAlumni.profile_pic)} 
                                    alt={selectedAlumni.name} 
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                />
                            ) : (
                                getInitials(selectedAlumni.name)
                            )}
                        </div>
                        
                        <h2 className="modal-name">{selectedAlumni.name}</h2>
                        
                        <div className="wing-badge" style={{background: '#134628', color: '#ffc300', margin: '0 auto 20px', display: 'inline-block'}}>
                            {selectedAlumni.wing} Wing
                        </div>

                        <div className="modal-details" style={{ textAlign: 'left', marginTop: '20px' }}>
    
                            {/* Basic Information */}
                            <div style={{ marginBottom: '20px' }}>
                                <div className="modal-detail-row">
                                    <i className="fas fa-envelope"></i> 
                                    <a href={`mailto:${selectedAlumni.email}`} className="email-link" style={{fontWeight: '600', fontSize: '1rem'}}>
                                        {selectedAlumni.email || "N/A"}
                                    </a>
                                </div>
                                <div className="modal-detail-row"><i className="fas fa-id-badge"></i> <span>Kit No: {selectedAlumni.kit_number || 'N/A'}</span></div>
                                <div className="modal-detail-row"><i className="fas fa-calendar-alt"></i> <span>Entry Year: {selectedAlumni.entry_year || 'N/A'}</span></div>
                                {selectedAlumni.entry_batch && (
                                    <div className="modal-detail-row"><i className="fas fa-layer-group"></i> <span>Batch: {selectedAlumni.entry_batch}</span></div>
                                )}
                                {selectedAlumni.contact_no && (
                                    <div className="modal-detail-row"><i className="fas fa-phone"></i> <span>Contact: {selectedAlumni.contact_no}</span></div>
                                )}
                            </div>

                            {/* Academic Information */}
                            {(selectedAlumni.education || selectedAlumni.institute) && (
                                <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px dashed #e2e8f0' }}>
                                    <span style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#94a3b8', fontWeight: '800', letterSpacing: '1px', display: 'block', marginBottom: '10px' }}>
                                        Academic Info
                                    </span>
                                    {selectedAlumni.education && (
                                        <div className="modal-detail-row"><i className="fas fa-user-graduate"></i> <span>{selectedAlumni.education}</span></div>
                                    )}
                                    {selectedAlumni.institute && (
                                        <div className="modal-detail-row"><i className="fas fa-university"></i> <span>{selectedAlumni.institute}</span></div>
                                    )}
                                </div>
                            )}

                            {/* Professional Information */}
                            {(selectedAlumni.current_status || selectedAlumni.profession || selectedAlumni.job_status || selectedAlumni.organization || selectedAlumni.designation) && (
                                <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px dashed #e2e8f0' }}>
                                    <span style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#94a3b8', fontWeight: '800', letterSpacing: '1px', display: 'block', marginBottom: '10px' }}>
                                        Professional Info
                                    </span>
                                    {selectedAlumni.current_status && (
                                        <div className="modal-detail-row"><i className="fas fa-info-circle"></i> <span>{selectedAlumni.current_status}</span></div>
                                    )}
                                    {selectedAlumni.profession && (
                                        <div className="modal-detail-row"><i className="fas fa-user-tie"></i> <span>{selectedAlumni.profession}</span></div>
                                    )}
                                    {(selectedAlumni.job_status || selectedAlumni.designation) && (
                                        <div className="modal-detail-row"><i className="fas fa-briefcase"></i> <span>{selectedAlumni.designation || selectedAlumni.job_status}</span></div>
                                    )}
                                    {selectedAlumni.organization && (
                                        <div className="modal-detail-row"><i className="fas fa-building"></i> <span>{selectedAlumni.organization}</span></div>
                                    )}
                                </div>
                            )}

                            {/* Address Information */}
                            {(selectedAlumni.current_address) && (
                                <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px dashed #e2e8f0' }}>
                                    <span style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#94a3b8', fontWeight: '800', letterSpacing: '1px', display: 'block', marginBottom: '10px' }}>
                                        Location
                                    </span>
                                    <div className="modal-detail-row"><i className="fas fa-map-marker-alt"></i> <span>{selectedAlumni.current_address}</span></div>
                                </div>
                            )}

                            {/* 🌟 NEW: Other Information (Skills & Achievements) */}
                            {(selectedAlumni.skills || selectedAlumni.achievements) && (
                                <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px dashed #e2e8f0' }}>
                                    <span style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#94a3b8', fontWeight: '800', letterSpacing: '1px', display: 'block', marginBottom: '10px' }}>
                                        Skills & Achievements
                                    </span>
                                    {selectedAlumni.skills && (
                                        <div className="modal-detail-row">
                                            <i className="fas fa-tools"></i> 
                                            <span>{selectedAlumni.skills}</span>
                                        </div>
                                    )}
                                    {selectedAlumni.achievements && (
                                        <div className="modal-detail-row">
                                            <i className="fas fa-trophy"></i> 
                                            <span style={{ whiteSpace: 'pre-line' }}>{selectedAlumni.achievements}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {selectedAlumni.email && (
                            <button 
                                className="modal-connect-btn" 
                                onClick={() => window.location.href = `mailto:${selectedAlumni.email}`}
                            >
                                <i className="fas fa-paper-plane me-2"></i> Connect via Email
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AlumniDirectory;